
  import Vue from 'vue';
  import {mapMutations, mapState} from 'vuex';
  import VueYoutube from 'vue-youtube';
  import {YoutubeIframe,
          YoutubeIframePlayer,
          YoutubePlayerState} from 'youtube-iframe-api';
  import * as VuexMutation from '@/store/mutation-types';
  import {Track} from '@/models/track';
  import {library} from '@/models/library';
  import Message from '@/models/message';
  import {secondsToTime} from '@/util';

  const emptyPlaylistNameErrorMessage = new Message(
    'error',
    'プレイリスト名が空欄です',
  );


  Vue.use(VueYoutube)

  export default Vue.extend({
    name: 'SharedPlayer',
    components: {
      Track: () => import('@/components/Track.vue'),
    },
    data() {
      return {
        playerVars: {
          autoplay: 0,
          controls: 0,
          fs: 0,
          rel: 0,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        playerState: -1,
        trackDuration: 0,
        trackCurrentTime: 0,
        trackProgress: 0,
        seekbarValue: 0,
        seekbarMax: 1000,
        nowSeeking: false,
        trackPlayed: false,
        playingTrack: null as Track | null,
        processId: null as number | null,
        savePlaylistDialog: false,
        newPlaylistName: '',
        newPlaylistErrorMessage: null as string | null,
        videoPlayed: false,
      }
    },

    computed: {
      ...mapState(['playerVolume', 'playerMute']),

      player(): YoutubeIframePlayer {
        const youtube = this.$refs.youtube as YoutubeIframe;
        return youtube.player as YoutubeIframePlayer;
      },

      tracks: {
        get(): Track[] {
          const query = this.$route.query;
          let tracks: Track[] = [];
          try {
            const trackIds = (query.tracks ? query.tracks.split('-') : []).map((id: string) => parseInt(id));
            tracks = library.getTracksByIds(trackIds);
          } catch (e) {
            window.alert('Invalid track ids');
          }
          return tracks;
        },
      },

      playlistName: {
        get(): string {
          const query = this.$route.query;
          return query.name;
        }
      },

      trackTimeOnSeekbar(): number {
        if (!this.playingTrack) return 0;
        const seekbarProgress = this.seekbarValue / this.seekbarMax;
        const elapsedTime= seekbarProgress * this.trackDuration;
        const time = this.playingTrack.start + elapsedTime;
        return time;
      },

      volume: {
        get(): number {
          return this.playerVolume;
        },
        set(volume: number) {
          this.setPlayerVolume(volume);
        },
      },

      volumeIcon(): string {
        if (this.playerMute) return 'mdi-volume-mute';
        return this.playerVolume === 0 ? 'mdi-volume-mute' : (
          this.playerVolume < 20 ? 'mdi-volume-low' :(
          this.playerVolume < 80 ? 'mdi-volume-medium' : 'mdi-volume-high'
        ));
      },
    },

    methods: {
      ...mapMutations({
        addFavoriteTrack: VuexMutation.ADD_FAVORITE_TRACK,
        removeFavoriteTrack: VuexMutation.REMOVE_FAVORITE_TRACK,
        setPlayerVolume: VuexMutation.SET_PLAYER_VOLUME,
        setPlayerMute: VuexMutation.SET_PLAYER_MUTE,
        addPlaylist: VuexMutation.ADD_PLAYLIST,
        addMessage: VuexMutation.ADD_MESSAGE,
      }),


      async loadTrack(track: Track) {
        this.playingTrack = track;
        await this.player.loadVideoById({
          'videoId': track.video.id,
          'startSeconds': track.start,
          'endSeconds': track.end,
        });
        this.trackDuration = track.end - track.start;
      },

      async playing() {
        if (!this.playingTrack) return;
        this.videoPlayed = true;
        this.trackPlayed = true;
        this.trackDuration = this.playingTrack.end - this.playingTrack.start;

        this.processId = setInterval(() => {
          if (!this.playingTrack) return;
          this.player.getCurrentTime().then(currentTime => {
            this.videoCurrentTime = currentTime;
            const elapsedTime = currentTime - this.playingTrack.start;
            const trackProgress = elapsedTime / this.trackDuration;

            this.trackProgress = trackProgress < 1 ? trackProgress : 1;
            if (!this.nowSeeking) {
              this.seekbarValue = this.seekbarMax * this.trackProgress;
            }
          });
        }, 100);
      },

      paused() {
        if (this.processId !== null) {
          clearInterval(this.processId);
        }
      },

      ended() {
        if (!this.videoPlayed) return;
        this.videoPlayed = false;
        this.playNext();
      },

      async playVideo() {
        if (!this.playingTrack) {
          this.playNext();
          return;
        }
        if (this.playerState === -1) {
          await this.loadTrack(this.playingTrack)
        }
        await this.player.playVideo();
      },

      async pauseVideo() {
        await this.player.pauseVideo();
      },

      playNext() {
        if (!this.playingTrack) return;
        const index = this.tracks.findIndex(track => track.uuid === this.playingTrack.uuid);
        if (index === -1) return;
        const nextIndex = index + 1;
        if (nextIndex === this.tracks.length) {
          return;
        }
        this.loadTrack(this.tracks[nextIndex]);
      },

      playPrev() {
        if (!this.playingTrack) return;
        const index = this.tracks.findIndex(track => track.uuid === this.playingTrack.uuid);
        if (index === -1) return;
        const prevIndex = index - 1;
        if (prevIndex < 0) {
          return;
        }
        this.loadTrack(this.tracks[prevIndex]);
      },

      seekStart() {
        this.nowSeeking = true;
      },

      async seekEnd() {
        this.nowSeeking = false;
        await this.player.seekTo(this.trackTimeOnSeekbar, true);
      },

      secondsToTime(t: number): string {
        return secondsToTime(t);
      },

      toggleFavorite() {
        if (!this.playingTrack) return;
        if (this.playingTrack.isFavorite) {
          this.removeFavoriteTrack(this.playingTrack)
          this.playingTrack.isFavorite = false;
        } else {
          this.addFavoriteTrack(this.playingTrack);
          this.playingTrack.isFavorite = true;
        }
      },

      async toggleMute() {
        this.setPlayerMute(!this.playerMute);
      },

      isNowPlaying(track: Track): boolean {
        if (!this.playingTrack) {
          return false;
        }
        let isNowPlaying = false;
        if (this.nowPlayingId === "uuid") {
          isNowPlaying = track.uuid === this.playingTrack.uuid;
        } else {
          isNowPlaying = track.id === this.playingTrack.id;
        }
        return isNowPlaying;
      },

      savePlaylist() {
        if (!this.newPlaylistName) {
          this.newPlaylistErrorMessage = emptyPlaylistNameErrorMessage.message;
          return;
        }
        const name = this.newPlaylistName;
        const tracks = this.tracks;
        const playlist = { name, tracks };
        try {
          this.addPlaylist(playlist);
        } catch(error) {
          this.newPlaylistErrorMessage = new Message(
            'error',
            error.message,
          );
          return;
        }
        this.savePlaylistDialog = false;
        this.addMessage(new Message(
          'info',
          `プレイリスト「${name}」を保存しました`,
        ));
      }
    },

    async mounted() {
      this.newPlaylistName = this.playlistName;

      await this.$nextTick(() => {
        this.player.addEventListener(
          'onStateChange',
          (state: YoutubePlayerState) => {
            this.playerState = state.data;
          }
        );
      });
      if (this.tracks.length > 0) {
        await this.loadTrack(this.tracks[0]);
      }
    },

    watch: {
      async playingTrack() {
        if (!this.playingTrack) {
          if (this.processId !== null) {
            clearInterval(this.processId);
          }
          await this.player.stopVideo();
          await this.player.loadVideoById('');
          return;
        }
        await this.loadTrack(this.playingTrack);
        await this.player.setVolume(this.playerVolume);
        if (this.playerMute) {
          await this.player.mute();
        } else {
          await this.player.unMute();
        }
        await this.player.playVideo();
      },

      async playerVolume() {
        await this.player.setVolume(this.playerVolume);
      },

      async playerMute() {
        if (this.playerMute) {
          await this.player.mute();
        } else {
          await this.player.unMute();
        }
      },
    }
  })
